import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'
import { ShoppingCart, Trash2, CreditCard, Loader2, ArrowLeft } from 'lucide-react'
import { getSession } from '../lib/session'
import { byKey, unitOf } from '../lib/serviceCatalog'
import { getCart, removeFromCart, clearCart } from '../lib/cart'

// Reached after sign-in (gated). Shows the cart and pays for all of it at once.
export default function Checkout() {
  const session = getSession()
  const navigate = useNavigate()
  const [cart, setCart] = useState(getCart())
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    const sync = () => setCart(getCart())
    window.addEventListener('reright-cart', sync)
    return () => window.removeEventListener('reright-cart', sync)
  }, [])
  const items = cart.map((c) => { const s = byKey(c.key); return s ? { ...s, qty: c.qty } : null }).filter(Boolean)
  const total = items.reduce((s, x) => s + x.price * x.qty, 0)

  const pay = async () => {
    if (!items.length) return
    setBusy(true)
    const lineItems = items.map((i) => ({ service: i.key, quantity: i.qty }))
    const services = items.map((i) => i.key)
    const soft = { validateStatus: () => true }
    try {
      const co = await axios.post('/api/v1/stripe/services-checkout', { namespaceId: session.namespaceId, items: lineItems }, soft)
      if (co.status < 400 && co.data?.url) { window.location.href = co.data.url; return } // → Stripe
      // Billing not configured → test-mode grant for each (with its seat count), then done.
      for (const i of items) {
        await axios.post(`/api/v1/tenants/${session.namespaceId}/services/${i.key}/activate`, { seats: i.qty }, soft)
        await axios.post(`/api/v1/tenants/${session.namespaceId}/services/${i.key}/confirm`, { simulatePayment: true }, soft)
      }
      clearCart()
      toast.success(`${services.length} service${services.length > 1 ? 's' : ''} activated`)
      navigate(`/welcome?activated=${encodeURIComponent(services.join(','))}`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Checkout failed'); setBusy(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ShoppingCart size={22} /> Checkout</h2>
      <p className="text-gray-500 mt-1">Review your services and pay. Cancel any one anytime.</p>

      {items.length === 0 ? (
        <div className="card mt-6 text-center py-12">
          <ShoppingCart size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-600">Your cart is empty.</p>
          <Link to="/home#services" className="text-primary-600 hover:underline text-sm mt-2 inline-block">Browse services →</Link>
        </div>
      ) : (
        <div className="card mt-6">
          <div className="divide-y divide-gray-100">
            {items.map((s) => {
              const Icon = s.icon
              return (
                <div key={s.key} className="flex items-center gap-3 py-3">
                  <div className="p-2 rounded-lg bg-primary-500/10"><Icon className="text-primary-600" size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{s.name}{s.qty > 1 && <span className="text-gray-400 font-normal"> × {s.qty} {unitOf(s.key)}s</span>}</p>
                    <p className="text-xs text-gray-500 truncate">{s.qty > 1 ? `$${s.price}/mo each` : s.short}</p>
                  </div>
                  <span className="text-sm text-gray-700 whitespace-nowrap tabular-nums">${s.price * s.qty}/mo</span>
                  <button onClick={() => removeFromCart(s.key)} className="text-gray-300 hover:text-red-500 p-1"><Trash2 size={15} /></button>
                </div>
              )
            })}
          </div>
          <div className="flex items-center justify-between pt-4 mt-1 border-t border-gray-200">
            <span className="font-semibold text-gray-900">Total</span>
            <span className="text-xl font-bold text-gray-900">${total}<span className="text-gray-400 text-sm font-normal">/mo</span></span>
          </div>
          <button onClick={pay} disabled={busy} className="btn-primary w-full mt-5 flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />} Pay ${total}/mo &amp; activate
          </button>
          <p className="text-xs text-center text-gray-400 mt-2">Secure payment via Stripe. You can add your website to each service afterward to train it.</p>
        </div>
      )}
      <Link to="/home#services" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mt-4"><ArrowLeft size={14} /> Keep browsing</Link>
    </div>
  )
}
